import {
  Checkbox,
  Divider,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  type ComboboxItem,
  type ComboboxLikeRenderOptionInput,
} from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { UseFormReturnType } from '@mantine/form';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { CreateUserData } from '../context/UsersContext';
import { USER_ROLES, UserRole } from '../types';
import { roleAsDescription, roleAsDisplayName } from 'src/admin/users/utils';

export type CreateUserFormValues = Omit<CreateUserData, 'userStatus'>;

const BRANDED_INPUT_STYLES = {
  label: {
    fontSize: 'var(--mantine-font-size-xs)',
    fontWeight: FONT_WEIGHTS.semibold,
    color: 'var(--mantine-color-gray-6)',
    marginBottom: 6,
  },
  input: {
    borderRadius: 12,
    border: '1px solid var(--mantine-color-gray-1)',
    boxShadow: 'var(--mantine-shadow-xs)',
  },
};

const BRANDED_SELECT_STYLES = {
  ...BRANDED_INPUT_STYLES,
  dropdown: {
    borderRadius: 16,
    backgroundColor: 'var(--mantine-color-white)',
    border: '1px solid var(--mantine-color-gray-1)',
    padding: '12px 4px',
  },
  options: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 4,
  },
  option: {
    padding: '4px 12px',
  },
};

const BRANDED_CHECKBOX_STYLES = {
  input: {
    width: 20,
    height: 20,
    borderRadius: 6,
    border: '1px solid var(--mantine-color-gray-3)',
  },
  labelWrapper: {
    paddingInlineStart: 12,
  },
};

function BrandedRoleOption({ option, checked }: ComboboxLikeRenderOptionInput<ComboboxItem>) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" gap="sm" w="100%">
      <Stack gap={2}>
        <Text size="sm" fw={FONT_WEIGHTS.medium} c={checked ? 'brand.6' : 'black'}>
          {option.label}
        </Text>
        <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.6">
          {roleAsDescription(option.value as UserRole)}
        </Text>
      </Stack>
      {checked && (
        <IconCheck size={20} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
      )}
    </Group>
  );
}

interface Props<T extends CreateUserFormValues> {
  form: UseFormReturnType<T>;
  showSecretMenuAccessOption: boolean;
  variant?: 'default' | 'branded';
}

export default function CreateUserForm<T extends CreateUserFormValues>({
  form,
  showSecretMenuAccessOption = false,
  variant = 'default',
}: Props<T>) {
  const branded = variant === 'branded';
  const inputStyles = branded ? BRANDED_INPUT_STYLES : undefined;

  return (
    <>
      <Group grow align="flex-start">
        <TextInput
          label="First name"
          placeholder="Jane"
          styles={inputStyles}
          {...form.getInputProps('firstName')}
        />
        <TextInput
          label="Last name"
          placeholder="Doe"
          styles={inputStyles}
          {...form.getInputProps('lastName')}
        />
      </Group>

      <TextInput
        label="Email"
        placeholder="Client email address"
        styles={inputStyles}
        {...form.getInputProps('emailAddress')}
      />

      {!branded && <Divider my="xs" color="gray.4" />}

      <Stack>
        <Select
          label="Permissions role"
          placeholder="Select permissions"
          styles={branded ? BRANDED_SELECT_STYLES : undefined}
          data={USER_ROLES.map(role => ({ label: roleAsDisplayName(role), value: role }))}
          renderOption={branded ? BrandedRoleOption : undefined}
          {...form.getInputProps('userRole')}
        />
      </Stack>

      {showSecretMenuAccessOption && (
        <Checkbox
          label={'Enable secret menu access'}
          styles={branded ? BRANDED_CHECKBOX_STYLES : undefined}
          {...form.getInputProps('isAdmin', { type: 'checkbox' })}
        />
      )}
    </>
  );
}
